import * as CSS from 'csstype'

import React, {type ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {defineStyles, useThemedStylesWithBreaks} from './styles/style'

import {CloseSmall} from './svg'
import {StyleValues} from './styles/types'
import {Theme} from './styles/theme'
import {useMouseover} from './hooks'

type PopoverListTheme = 'standard' | 'spillover' | 'spillover-right'
type PopoverItemTheme = 'standard' | 'alert'

const separatorHeight = `1px`
const separatorColor = ({core}: Theme) => {
  return core.b3
}

export const popoverItemHoverTextColor = ({accent}: Theme) => {
  return accent.container
}

export const defaultItemVerticalPadding = `10px`
export const endItemVerticalPadding = `14px`

export interface PopoverListItem {
  /** Text or element to render in the vertical list */
  /** Note: When content is set to 'SeparatorItem', all other properties are ignored except for 'style'. */
  content: ReactNode | string | 'SeparatorItem' | ((isMouseOver: boolean) => ReactElement)

  /** Creates a colored icon to place to the left of the text. */
  iconCreator?: (color: CSS.Property.Color) => ReactElement

  /** Defines an icon color to use when the item is not hovered. */
  iconColor?: CSS.Property.Color

  /** (optional) Style values to be applied to the item */
  style?: StyleValues

  /** (optional) Style values applied to an item on mouse-over */
  hoverStyle?: StyleValues

  /** (optional) Action to perform when clicking on the item */
  clickAction?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void

  /** Determines the style of the item. */
  theme?: PopoverItemTheme

  /** (optional) Applies hoverStyle even when the mouse isn't over the item, e.g. for keyboard-driven selection. */
  selected?: boolean
}

interface PopoverListProps {
  /** Items to be displayed in the list. */
  items: PopoverListItem[]

  /** (optional) header banner for marketing narrow menu*/
  headerBanner?: ReactElement

  /** (optional) Title to be displayed in the list. */
  title?: string

  /** (optional) Style to be applied at the top-level of hte list. */
  style?: StyleValues

  /** (optional) Action to be called when the 'close' button is pressed, or when the user clicks outside of the popover. */
  onClose?: () => void

  /** (optional) Shows a close button if set to true and onClose is defined. */
  showCloseButton?: boolean

  /** (optional) If an element up the component hierarchy from the popover is capable of toggling the popover open and closed, you need to pass a ref to it so that the useOnClickOutside hook will not count clicks on it as being outside of the popover. If you don't do this, and the popover is open and you click such an element, the popover will not toggle closed as expected. */
  externalToggleRef?: React.MutableRefObject<HTMLElement | null>

  /** (optional) If an element up the component hierarchy from the popover needs access to accurate popover content height, pass a ref in here  */
  contentHeightRef?: React.MutableRefObject<HTMLDivElement | null>

  /** Height down to which the menu collapses when not expanded. */
  collapseHeight?: string

  /** Icon you'd click on to collapse the menu. */
  collapseIcon?: ReactElement

  /** Classname to apply to the top level component */
  className?: string

  /** Margin on the left side. */
  headerStyle?: StyleValues

  /** True to highlight items as you mouse over them.  Default: false */
  highlightMouseover?: boolean

  /** onMouseDown for top div in list */
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>

  /** Determines the style of the list. */
  theme?: PopoverListTheme
}

interface PopoverListItemProps {
  item: PopoverListItem
  index: number
}

const styles = defineStyles((theme, font) => {
  const collapseIconContainerVisible = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1
  }

  return {
    header: {
      ...font.largeTitleBold,
      padding: '32px 32px 12px 43px',
      display: 'flex',
      justifyContent: 'space-between',
      textTransform: 'uppercase',
      alignItems: 'center',
      closeIcon: {
        fill: theme.core.f1
      }
    },

    option: {
      width: '100%',
      fontWeight: 500,
      fontSize: '18px',
      color: theme.core.f2,
      padding: `${defaultItemVerticalPadding} 10px ${defaultItemVerticalPadding} 43px`,
      minWidth: '150px',
      margin: '0',
      whiteSpace: 'nowrap',
      borderBottom: `${separatorHeight} solid ${separatorColor(theme)}`
    },

    spilloverOption: {
      fontWeight: 600,
      padding: `${defaultItemVerticalPadding} 20px`,
      fontSize: '16px',
      borderBottom: undefined
    },

    optionSeparator: {
      height: separatorHeight,
      margin: `${defaultItemVerticalPadding} 0px`,
      padding: '0px',
      backgroundColor: separatorColor(theme),
      width: '100%'
    },

    optionClickable: {
      cursor: 'pointer'
    },

    optionAlert: {
      color: theme.alert.base
    },

    optionAlertActive: {
      backgroundColor: theme.alert.hover,
      color: theme.accent.container
    },

    optionFirst: {
      paddingTop: `${endItemVerticalPadding}`
    },

    optionLast: {
      paddingBottom: `${endItemVerticalPadding}`,
      marginBottom: undefined
    },

    popoverContainer: {
      position: 'absolute',
      left: '0px',
      width: `100%`,
      zIndex: 10,
      backgroundColor: theme.core.b1
    },

    popoverContainerRight: {
      position: 'relative',
      right: '0px'
    },

    popoverContainerCollapsible: {
      overflow: 'hidden'
    },

    optionsContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-around',
      alignItems: 'flex-start',
      userSelect: 'none'
    },

    imageContainer: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer'
    },

    iconItem: {
      display: 'flex',
      justifyContent: 'space-between'
    },

    activeItem: {
      backgroundColor: theme.accent.base,
      color: popoverItemHoverTextColor(theme),
      fontWeight: 'bold'
    },

    contentRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px'
    },

    collapseIconContainerVisible,
    collapseIconContainerHidden: {
      ...collapseIconContainerVisible,
      opacity: 0
    }
  }
})

/**
 * Creates a vertical list of options to be rendered at the full width of its container, extending downward.
 * Options can have click actions, can be text or elements, can apply individual style.
 *
 * @param items Items to be rendered inside the popover, rendered from top to bottom.
 * @param title (optional) Title to be displayed at the top of the popover
 * @param style (optional) Style to be applied at the top-level of the popover
 * @param onClose (optional) Function that when called, will close the popover
 */
export function PopoverList({
  title,
  headerBanner,
  items,
  style,
  onClose,
  showCloseButton,
  externalToggleRef,
  contentHeightRef,
  collapseHeight,
  collapseIcon,
  className,
  headerStyle,
  highlightMouseover,
  onMouseDown,
  theme = 'standard'
}: PopoverListProps) {
  const s = useThemedStylesWithBreaks(styles)

  const [isCollapsed, setCollapsed] = useState(!!collapseHeight)

  const containerRef = useRef<HTMLDivElement>(null)
  const onClickOutside = useCallback(() => {
    if (collapseHeight && !isCollapsed) {
      setCollapsed(true)
    }
    onClose?.()
  }, [collapseHeight, isCollapsed, onClose])
  useOnClickOutsidePopover(onClickOutside, containerRef, externalToggleRef)

  const ItemComponent = ({index, item}: PopoverListItemProps) => {
    const itemStyle = useMemo<React.CSSProperties>(() => {
      const style = {
        ...s.option,
        ...(theme === 'spillover' || theme === 'spillover-right' ? s.spilloverOption : undefined),
        ...(item.theme === 'alert' ? s.optionAlert : undefined),
        ...(index === 0 ? s.optionFirst : undefined),
        ...(index === items.length - 1 ? s.optionLast : undefined),
        ...(item.clickAction ? s.optionClickable : undefined),
        ...(item.content === 'SeparatorItem' ? s.optionSeparator : undefined),
        ...item.style
      }
      return style
    }, [index, item])

    const itemProps = {
      style: itemStyle,
      key: index,
      onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (isCollapsed) {
          setCollapsed(false)
        } else {
          item.clickAction && item.clickAction(e)
          !!collapseHeight && setCollapsed(true)
        }
      }
    }

    // If hoverstyle is provided use that, otherwise if highlight mouseover, use that.  Otherwise nothing.
    const hoverStyle = useMemo<StyleValues>(() => {
      let hStyles: StyleValues = {}

      if (item.content !== 'SeparatorItem') {
        if (item.hoverStyle) {
          hStyles = item.hoverStyle
        } else if (item.theme === 'alert') {
          hStyles = {...s.activeItem, ...s.optionAlertActive}
        } else if (highlightMouseover || theme === 'spillover' || theme === 'spillover-right') {
          hStyles = s.activeItem
        }
      }
      return hStyles
    }, [item.content, item.hoverStyle, item.theme])

    const [isMouseOver, mouseListeners] = useMouseover()
    const isHighlighted = isMouseOver || !!item.selected

    const content =
      typeof item.content === 'function'
        ? item.content(isHighlighted)
        : item.content === 'SeparatorItem'
          ? null
          : item.content

    const contentRow = (
      <div style={s.contentRow}>
        {item.iconCreator
          ? item.iconCreator(
              isHighlighted
                ? ((hoverStyle as any).color ?? itemProps.style.color)
                : (item.iconColor ?? itemProps.style.color)
            )
          : undefined}
        {content}
      </div>
    )

    const hoverableItemStyle = useMemo(
      () => ({...itemProps.style, ...(isHighlighted && {...hoverStyle})}),
      [hoverStyle, isHighlighted, itemProps.style]
    )

    const collapsibleIconStyle = useMemo(() => ({...s.iconItem, ...itemProps.style}), [itemProps.style])

    if (hoverStyle && (!isCollapsed || !collapseHeight)) {
      return (
        <div {...itemProps} {...mouseListeners} style={hoverableItemStyle}>
          {contentRow}
        </div>
      )
    } else if (collapseIcon) {
      // Render, but don't show the collapse icon on non-first items, so the collapsed view
      // is ready to accomodate the space those items take once chosen.
      return (
        <div {...itemProps} style={collapsibleIconStyle}>
          <div>{contentRow}</div>
          <div style={index === 0 ? s.collapseIconContainerVisible : s.collapseIconContainerHidden}>{collapseIcon}</div>
        </div>
      )
    } else {
      return <div {...itemProps}>{content}</div>
    }
  }

  const container = (
    <div style={s.optionsContainer} ref={contentHeightRef}>
      {items.map((item, index) => (
        <ItemComponent key={index} item={item} index={index} />
      ))}
    </div>
  )

  const _showCloseButton = onClose && showCloseButton

  const buttonStyle = useMemo(() => {
    return {
      ...(theme === 'spillover-right' ? s.popoverContainerRight : s.popoverContainer),
      ...(!!collapseHeight ? s.popoverContainerCollapsible : {}),
      ...style,
      ...(collapseHeight &&
        isCollapsed && {
          height: collapseHeight,
          zIndex: 0
        })
    }
  }, [theme, isCollapsed, s, collapseHeight, style])

  return (
    <div ref={containerRef} onMouseDown={onMouseDown} className={className} style={buttonStyle}>
      {headerBanner !== undefined ? (
        <div style={{...s.header, ...(headerStyle ?? {}), textTransform: 'none'}}> {headerBanner} </div>
      ) : (
        (title || _showCloseButton) && (
          <div style={{...s.header, ...(headerStyle ?? {})}}>
            {title}
            {_showCloseButton ? (
              <div style={s.imageContainer} onClick={onClose}>
                <CloseSmall color={s.header.closeIcon.fill} />
              </div>
            ) : null}
          </div>
        )
      )}
      {container}
    </div>
  )
}

export function useOnClickOutsidePopover(
  onClickOutside: (e: PointerEvent) => void,
  popoverRef: React.MutableRefObject<HTMLElement | null>,
  externalToggleRef?: React.MutableRefObject<HTMLElement | null>
) {
  useEffect(() => {
    const listener = (event: PointerEvent) => {
      if (!popoverRef.current || popoverRef.current.contains(event.target as Node)) {
        return
      }

      if (externalToggleRef?.current?.contains(event.target as Node)) {
        return
      }

      onClickOutside(event)
    }
    document.addEventListener('pointerdown', listener)
    return () => {
      document.removeEventListener('pointerdown', listener)
    }
  }, [onClickOutside, popoverRef, externalToggleRef])
}
